import { useCallback, useEffect, useId, type ReactNode } from "react"
import { createPortal } from "react-dom"

import { useInspectorDrilldownInternal } from "~/components/design-system/pages/internal/InspectorDrilldownContext"
import { type InspectorWidth } from "~/components/design-system/pages/types"

/**
 * Deeper detail for an inspector item, as a nested view INSIDE the inspector: `trigger` renders
 * the control that opens it, `children` the content. The view replaces the current one (with a
 * back button) at the inspector's width, so the table beside it keeps its space. Drilldowns nest;
 * back pops one level at a time.
 *
 * `expandableAt` adds an expand control; the content branches on the same threshold via
 * `useInspectorWidth`. Content is portalled from THIS position, so it re-renders with the owner's
 * props (an imperative `open(<Detail/>)` would be a stale snapshot). `title` is a plain string,
 * read on open, so keep it static.
 *
 * Inspector content only (needs a `<Module>` above). Replaces hand-rolling a drawer for content that
 * already lives in an inspector.
 *
 * Canonical intent: {@link ../intents.ts INSPECTOR_DRILLDOWN_INTENT}. Approved surface: APPROVED.md.
 *
 * @example
 * <InspectorDrilldown
 *   title="Size split"
 *   trigger={({ open }) => (
 *     <Button size="xs" variant="neutral" onClick={open}>
 *       Edit
 *     </Button>
 *   )}
 * >
 *   <SizeSplitDrilldown variantId={variantId} />
 * </InspectorDrilldown>
 */
const InspectorDrilldown = ({
  title,
  minWidth,
  expandableAt,
  trigger,
  children,
}: {
  title?: string
  /**
   * Px floor while this level is open, for content that can't work at the inspector's width. The
   * user's width wins when larger; released on back.
   */
  minWidth?: number
  /**
   * The px width at which this level's extra content appears (the content's
   * `useInspectorWidth().width` threshold). The expand control hides when a narrow viewport can't
   * reach it.
   */
  expandableAt?: number
  /** `isOpen` is true only while this level is on show; a parent reports false under a child. */
  trigger: (props: { open: () => void; close: () => void; isOpen: boolean }) => ReactNode
  children: ReactNode
  /** @deprecated No longer used: a drilldown follows the inspector's width. Pass `minWidth` instead. */
  width?: InspectorWidth
}) => {
  const id = useId()
  const { stack, open, back, remove } = useInspectorDrilldownInternal()
  useEffect(() => () => remove(id), [remove, id])
  const level = stack.findIndex((entry) => entry.key === id)
  const isInStack = level !== -1
  const isOpen = isInStack && level === stack.length - 1
  const container = isInStack ? stack[level]?.container : null
  const openThis = useCallback(
    () => open(id, title, minWidth, expandableAt),
    [open, id, title, minWidth, expandableAt],
  )

  return (
    <>
      {trigger({ open: openThis, close: back, isOpen })}
      {/* Presence in the stack, not "on top": a parent's view must stay mounted under a child. */}
      {isInStack && container ? createPortal(children, container) : null}
    </>
  )
}

export default InspectorDrilldown
